import { CLOSING_SOON_THRESHOLD_SECONDS } from 'features/locations/location.constants';
import {
  HourStatus,
  OperatingTime,
  OperatingTimespansForDay,
} from 'features/locations/location.types';
import {
  buildInstantForZonedDay,
  DAY_IN_SECONDS,
  getDatePartsInTimeZone,
  getLocalWeekdayAndSecondsInTimeZone,
  getSecondsSinceMidnight,
  getUserTimeZone,
  resolveLocationTimeZone,
  TimeZoneDateParts,
} from './timeZone';
import {
  ScheduleContext,
  ScheduleByDay,
  ViewerNow,
  DaySegment,
  ViewerOperatingHours,
  CurrentHourDetails,
  LocationOperatingStatusInput,
} from './operatingHours.types';

const buildScheduleContext = (
  locationTimeZone: string | undefined,
): ScheduleContext => {
  const now = new Date();
  const viewerTz = getUserTimeZone();
  const locationTz = resolveLocationTimeZone(locationTimeZone);
  const nowPartsInLocationTz = getDatePartsInTimeZone(now, locationTz);
  const nowPartsInViewerTz = getDatePartsInTimeZone(now, viewerTz);
  const viewerNow = getLocalWeekdayAndSecondsInTimeZone(viewerTz, now);

  return {
    now,
    viewerTz,
    locationTz,
    nowPartsInLocationTz,
    nowPartsInViewerTz,
    viewerNow,
  };
};

const buildScheduleByDay = (
  schedule: OperatingTimespansForDay[],
): ScheduleByDay => new Map(schedule.map((entry) => [entry.dayOfWeek, entry]));

const findActiveSlot = (
  daySchedule: OperatingTimespansForDay,
  secondsSinceMidnight: number,
): OperatingTime | undefined =>
  daySchedule.times.find(
    (time) =>
      time.openTime <= secondsSinceMidnight &&
      time.closeTime > secondsSinceMidnight,
  );

/**
 * After `splitRangeAtUserMidnight`, a slot that ends at `DAY_IN_SECONDS` may
 * continue on the next calendar day as a segment with `openTime === 0`.
 */
const findMidnightSplitContinuation = (
  scheduleByDay: ScheduleByDay,
  nextDayOfWeek: number,
): OperatingTime | undefined => {
  const nextDay = scheduleByDay.get(nextDayOfWeek);
  return nextDay?.times.find(
    (time) => time.openTime === 0 && time.closeTime > 0,
  );
};

const deriveTodayViewerState = (
  hoursByViewerDay: ScheduleByDay,
  viewerNow: ViewerNow,
): {
  todayStatus: HourStatus;
  activeSlot?: OperatingTime;
} => {
  const { dayOfWeek, secondsSinceMidnight } = viewerNow;
  const todayHours = hoursByViewerDay.get(dayOfWeek);
  if (!todayHours) {
    return { todayStatus: 'closed' };
  }
  const activeSlot = findActiveSlot(todayHours, secondsSinceMidnight);
  if (!activeSlot) {
    return { todayStatus: 'closed' };
  }

  // After converting ranges and splitting at midnight, a slot that continues
  // past midnight becomes an "until midnight" segment (ending at DAY_IN_SECONDS)
  // followed by a next-day continuation starting at 0. The UI label uses the
  // *final* close (e.g. "Closes tomorrow at 3:00 AM"), so the chip status must
  // also evaluate "closing soon" against that final close, not midnight.
  let secondsUntilClose: number;
  if (activeSlot.closeTime !== DAY_IN_SECONDS) {
    secondsUntilClose = activeSlot.closeTime - secondsSinceMidnight;
  } else {
    const tomorrowDayOfWeek = (dayOfWeek + 1) % 7;
    const continuation = findMidnightSplitContinuation(
      hoursByViewerDay,
      tomorrowDayOfWeek,
    );
    secondsUntilClose = continuation
      ? DAY_IN_SECONDS - secondsSinceMidnight + continuation.closeTime
      : DAY_IN_SECONDS - secondsSinceMidnight;
  }

  const todayStatus: HourStatus =
    secondsUntilClose <= CLOSING_SOON_THRESHOLD_SECONDS
      ? 'closingSoon'
      : 'open';
  return { todayStatus, activeSlot };
};

const getDateForScheduleDaySecondsAt = (
  dayOfWeek: number,
  secondsSinceMidnight: number,
  resolvedScheduleTimeZone: string,
  nowPartsInScheduleTimeZone: TimeZoneDateParts,
): Date => {
  const daysFromToday = dayOfWeek - nowPartsInScheduleTimeZone.weekday;
  return buildInstantForZonedDay(
    nowPartsInScheduleTimeZone,
    daysFromToday,
    secondsSinceMidnight,
    resolvedScheduleTimeZone,
  );
};

/**
 * Splits a (openParts, closeParts) pair — both expressed in the user's
 * timezone — into one or two day-anchored segments. If the interval stays
 * inside a single calendar day it becomes one segment; otherwise it is split
 * at midnight so each segment fits on one day. Zero-duration segments (which
 * arise from midnight-split arithmetic) are dropped.
 */
const splitRangeAtUserMidnight = (
  openParts: TimeZoneDateParts,
  closeParts: TimeZoneDateParts,
): DaySegment[] => {
  const openSeconds = getSecondsSinceMidnight(openParts);
  const closeSeconds = getSecondsSinceMidnight(closeParts);
  const staysWithinSingleDay =
    openParts.weekday === closeParts.weekday && closeSeconds > openSeconds;

  if (staysWithinSingleDay) {
    return [
      {
        dayOfWeek: openParts.weekday,
        openTime: openSeconds,
        closeTime: closeSeconds,
      },
    ];
  }

  const openingDaySegment: DaySegment = {
    dayOfWeek: openParts.weekday,
    openTime: openSeconds,
    closeTime: DAY_IN_SECONDS,
  };
  const closingDaySegment: DaySegment = {
    dayOfWeek: closeParts.weekday,
    openTime: 0,
    closeTime: closeSeconds,
  };
  return [openingDaySegment, closingDaySegment].filter(
    (segment) => segment.openTime !== segment.closeTime,
  );
};

const convertLocationRangeToUserSegments = (
  dayOfWeek: number,
  operatingTime: OperatingTime,
  ctx: ScheduleContext,
): DaySegment[] => {
  const openInstant = getDateForScheduleDaySecondsAt(
    dayOfWeek,
    operatingTime.openTime,
    ctx.locationTz,
    ctx.nowPartsInLocationTz,
  );
  const closeInstant = getDateForScheduleDaySecondsAt(
    dayOfWeek,
    operatingTime.closeTime,
    ctx.locationTz,
    ctx.nowPartsInLocationTz,
  );

  const openParts = getDatePartsInTimeZone(openInstant, ctx.viewerTz);
  const closeParts = getDatePartsInTimeZone(closeInstant, ctx.viewerTz);
  return splitRangeAtUserMidnight(openParts, closeParts);
};

const groupSegmentsByDay = (
  segments: DaySegment[],
): OperatingTimespansForDay[] => {
  const byDay = new Map<number, OperatingTime[]>();
  for (const { dayOfWeek, openTime, closeTime } of segments) {
    const times = byDay.get(dayOfWeek) ?? [];
    times.push({ openTime, closeTime });
    byDay.set(dayOfWeek, times);
  }

  return Array.from(byDay.entries())
    .map(([dayOfWeek, times]) => ({
      dayOfWeek,
      times: [...times].sort(
        (first, second) => first.openTime - second.openTime,
      ),
    }))
    .sort((first, second) => first.dayOfWeek - second.dayOfWeek);
};

/**
 * Re-expresses the location's schedule in the viewer's local timezone.
 */
const convertOperatingHoursToUserTimeZoneAt = (
  hours: OperatingTimespansForDay[],
  ctx: ScheduleContext,
): OperatingTimespansForDay[] => {
  const segments = hours.flatMap((dayHours) =>
    dayHours.times.flatMap((operatingTime) =>
      convertLocationRangeToUserSegments(
        dayHours.dayOfWeek,
        operatingTime,
        ctx,
      ),
    ),
  );

  return groupSegmentsByDay(segments);
};

/**
 * Returns the next opening as an absolute UTC Date, or null if no opening
 * is found within the next 7 days.
 */
const getNextOpeningDateForScheduleAt = (
  hoursByDay: ScheduleByDay,
  resolvedScheduleTimeZone: string,
  nowPartsInScheduleTimeZone: TimeZoneDateParts,
): Date | null => {
  const secondsNow = getSecondsSinceMidnight(nowPartsInScheduleTimeZone);

  for (let dayOffset = 0; dayOffset <= 7; dayOffset += 1) {
    const targetDayOfWeek =
      (nowPartsInScheduleTimeZone.weekday + dayOffset) % 7;
    const dayHours = hoursByDay.get(targetDayOfWeek);
    const isToday = dayOffset === 0;
    const nextSlot = dayHours?.times.find(
      (range) => !isToday || range.openTime > secondsNow,
    );

    if (nextSlot) {
      return buildInstantForZonedDay(
        nowPartsInScheduleTimeZone,
        dayOffset,
        nextSlot.openTime,
        resolvedScheduleTimeZone,
      );
    }
  }

  return null;
};

const resolveViewerCloseAtForActiveSlot = (
  activeSlot: OperatingTime,
  todayDayOfWeek: number,
  hoursByViewerDay: ScheduleByDay,
  ctx: ScheduleContext,
): Date => {
  if (activeSlot.closeTime !== DAY_IN_SECONDS) {
    return getDateForScheduleDaySecondsAt(
      todayDayOfWeek,
      activeSlot.closeTime,
      ctx.viewerTz,
      ctx.nowPartsInViewerTz,
    );
  }

  const tomorrowDayOfWeek = (todayDayOfWeek + 1) % 7;
  const continuation = findMidnightSplitContinuation(
    hoursByViewerDay,
    tomorrowDayOfWeek,
  );

  if (continuation) {
    return getDateForScheduleDaySecondsAt(
      tomorrowDayOfWeek,
      continuation.closeTime,
      ctx.viewerTz,
      ctx.nowPartsInViewerTz,
    );
  }

  return getDateForScheduleDaySecondsAt(
    todayDayOfWeek,
    activeSlot.closeTime,
    ctx.viewerTz,
    ctx.nowPartsInViewerTz,
  );
};

// Converts location hours to the viewer zone and derives chip state.
export const getOperatingHoursForViewer = (
  hours: OperatingTimespansForDay[],
  locationTimeZone: string | undefined,
): ViewerOperatingHours => {
  const ctx = buildScheduleContext(locationTimeZone);
  const hoursInUserTimeZone = convertOperatingHoursToUserTimeZoneAt(hours, ctx);
  const hoursByUserDay = buildScheduleByDay(hoursInUserTimeZone);
  const { todayStatus, activeSlot } = deriveTodayViewerState(
    hoursByUserDay,
    ctx.viewerNow,
  );
  return {
    ctx,
    hoursInUserTimeZone,
    hoursByUserDay,
    todayStatus,
    activeSlot,
    viewerDayOfWeek: ctx.viewerNow.dayOfWeek,
  };
};

export const getLocationStatusForViewer = (
  location: LocationOperatingStatusInput,
): HourStatus =>
  getOperatingHoursForViewer(location.data.hours, location.data.timezone)
    .todayStatus;

/**
 * Returns close time, next opening, and chip status for the location header.
 * `hours` are raw location-zone schedule rows; timezone conversion happens internally.
 */
export const getCurrentHourDetails = (
  hours: OperatingTimespansForDay[],
  locationTimeZone: string | undefined,
): CurrentHourDetails => {
  const { ctx, hoursByUserDay, todayStatus, activeSlot } =
    getOperatingHoursForViewer(hours, locationTimeZone);

  if (activeSlot) {
    const closeAt = resolveViewerCloseAtForActiveSlot(
      activeSlot,
      ctx.viewerNow.dayOfWeek,
      hoursByUserDay,
      ctx,
    );
    return { status: todayStatus, closeAt, nextOpenAt: null, now: ctx.now };
  }

  const nextOpenAt = getNextOpeningDateForScheduleAt(
    hoursByUserDay,
    ctx.viewerTz,
    ctx.nowPartsInViewerTz,
  );

  return {
    status: todayStatus,
    closeAt: null,
    nextOpenAt,
    now: ctx.now,
  };
};
